import { isValidSSN, isValidName } from 'platform/forms/validations';

// Returns an error message string, or null if valid.
// All validators treat null/'' as valid (field not required at this layer).

export const validateSsn = v => {
  if (v == null || v === '') return null;
  return isValidSSN(v) ? null : 'Enter a valid Social Security number';
};

const MIN_YEAR = 1900;

// Validate an ISO YYYY-MM-DD string. Returns null when valid, otherwise an
// error message. Messages can be customized per failure type:
//   required   – value is empty/missing (defaults to null, i.e. optional here)
//   invalid    – malformed or impossible date (missing/NaN parts, e.g. Feb 31),
//                or a date in the current year that is later than today
//   outOfRange – the year is outside the allowed 1900–currentYear range
export const validateIsoDate = (v, messages = {}) => {
  const currentYear = new Date().getFullYear();
  const required = messages.required ?? null;
  const invalid = messages.invalid ?? 'Provide a valid current or past date';
  const outOfRange =
    messages.outOfRange ??
    `Enter a year between ${MIN_YEAR} and ${currentYear}`;

  if (v == null || v === '') return required;

  const [year, month, day] = String(v).split('-');
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);

  // All three parts must be present and numeric.
  if (
    !year ||
    !month ||
    !day ||
    Number.isNaN(y) ||
    Number.isNaN(m) ||
    Number.isNaN(d)
  ) {
    return invalid;
  }

  // Reject impossible dates by round-tripping through the Date object
  // (JS rolls invalid values over, e.g. 2020-02-31 becomes March 2).
  const date = new Date(y, m - 1, d);
  const isRealDate =
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d;
  if (!isRealDate) return invalid;

  // Structurally valid — enforce the allowed range and no-future rule.
  if (y < MIN_YEAR || y > currentYear) return outOfRange;
  if (date > new Date()) return invalid;

  return null;
};

export const validateNamePart = (v, label = 'name') => {
  if (v == null || v === '') return null;
  return isValidName(v) ? null : `Enter a valid ${label}`;
};

export const validateText = (
  v,
  { label = 'field', min = 0, max = Infinity } = {},
) => {
  if (v == null || v === '') return null;
  const len = v.trim().length;
  if (min > 0 && len < min)
    return `${label} must be at least ${min} characters`;
  if (len > max) return `${label} must be ${max} characters or fewer`;
  return null;
};

// Safety-net enum check — primary UX uses constrained selects/combo-boxes
export const validateEnum = (v, allowed, label = 'value') => {
  if (v == null || v === '') return null;
  const set = Array.isArray(allowed) ? allowed : [...allowed];
  return set.includes(v) ? null : `Select a valid ${label}`;
};
